import { v4 as uuidv4 } from 'uuid';
import { debounce } from 'lodash';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, getDoc, runTransaction } from 'firebase/firestore';
import { db, auth } from '@/firebaseConfig';
import { currentWriterUid } from '@/utils/currentWriterUid';
import { app } from '@/main'; // Assuming firebase is initialized and exported from here

const STALL_TIMEOUT_MS = 45000; // Consider stalled if no progress for 45s
const BATCH_WRITE_DELAY_MS = 500; // Delay before flushing batched writes

const state = {
  uploads: [],
  overall: {
    total: 0,
    completed: 0,
  },
  wakeLock: null,
  visibilityWarningShown: false,
  // Batched writes: { [projectId]: { enterpriseID, writes: [{ uploadId, modelUuid, newModel, replace, file, projectLabeling }] } }
  pendingModelWrites: {},
};

// Module-level debounced flush function (initialized lazily with store reference)
let debouncedFlush = null;

// Helper to acquire wake lock to prevent tab throttling during uploads
async function acquireWakeLock(store) {
  // Try Wake Lock API first (keeps screen awake and prevents aggressive throttling)
  if ('wakeLock' in navigator) {
    try {
      const wakeLock = await navigator.wakeLock.request('screen');
      store.commit('SET_WAKE_LOCK', wakeLock);
      wakeLock.addEventListener('release', () => {
      });
      return true;
    } catch (err) {
      console.warn('Wake Lock API failed:', err);
    }
  }
  return false;
}

// Release wake lock when all uploads complete
function releaseWakeLock(store) {
  if (store.state.wakeLock) {
    try {
      store.state.wakeLock.release();
      store.commit('SET_WAKE_LOCK', null);
    } catch (err) {
      console.warn('Failed to release wake lock:', err);
    }
  }
}

// Setup page visibility handler to warn about backgrounding during uploads
let visibilityHandlerInstalled = false;
function ensureVisibilityHandler(store) {
  if (visibilityHandlerInstalled || typeof document === 'undefined') return;
  visibilityHandlerInstalled = true;

  document.addEventListener('visibilitychange', () => {
    const hasActiveUploads = store.state.uploads.some(u => u.status === 'uploading' || u.status === 'queued');

    if (document.hidden && hasActiveUploads && !store.state.visibilityWarningShown) {
      console.warn('Tab is now hidden. Uploads will continue but may be slowed by browser throttling.');
      store.commit('SET_VISIBILITY_WARNING_SHOWN', true);
      // Emit event so UI can show a notification if desired
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('upload:tab-backgrounded', {
          detail: { message: 'Uploads continue in background but may be slower' }
        }));
      }
    }

    if (!document.hidden) {
      store.commit('SET_VISIBILITY_WARNING_SHOWN', false);
    }
  });
}

const getters = {
  uploads: (state) => state.uploads,
  overallProgress: (state) => {
    // Progress across the original queue size: completed count + current item progress
    if (!state.overall.total) return 0;
    const current = state.uploads.find(u => u.status === 'uploading');
    const currentProgress = current ? (current.progress || 0) : 0;
    const numerator = (state.overall.completed * 100) + currentProgress;
    const percent = numerator / state.overall.total;
    return Math.max(0, Math.min(100, percent));
  },
  currentProgress: (state) => {
    const current = state.uploads.find(u => u.status === 'uploading');
    return current ? current.progress : 0;
  },
};

/**
 * Stamp a finished attachment's URL onto the project doc's `attachments[]` (fresh read, so a concurrent
 * upload that raced in survives). `lastUpdatedBy` is the sender at send time: firestore.rules (F-113)
 * refuses a guest's update that leaves the previous writer's stamp in place.
 */
export async function persistAttachmentCompletion ({ enterpriseID, projectId, attachmentUuid, file, downloadURL }) {
  const projectRef = doc(db, 'enterprises', enterpriseID, 'projects', projectId);
  const snap = await getDoc(projectRef);
  if (!snap.exists()) return;
  const data = snap.data() || {};
  const attachments = Array.isArray(data.attachments) ? [...data.attachments] : [];
  const idx = attachments.findIndex(a => a && a.uuid === attachmentUuid);
  if (idx >= 0) {
    attachments[idx] = Object.assign({}, attachments[idx], { url: downloadURL, uploadProgress: 100 });
  } else {
    attachments.push({
      uuid: attachmentUuid,
      name: file.name,
      size: file.size,
      url: downloadURL,
      linked: [],
      dateCreated: Date.now(),
    });
  }
  await updateDoc(projectRef, { attachments, lastUpdatedBy: currentWriterUid() });
}

const actions = {
  addUpload({ commit, dispatch, state }, { file, projectId, partId, replace, modelData, enterpriseID, type = 'model', storagePath = null, jobId = null, attachmentUuid = null, projectLabeling = undefined }) {
    // Validate required parameters
    if (!projectId) {
      console.error('addUpload: projectId is required but was undefined');
      return;
    }
    if (!partId) {
      console.error('addUpload: partId is required but was undefined');
      return;
    }
    if (!file) {
      console.error('addUpload: file is required but was undefined');
      return;
    }
    if (!enterpriseID) {
      console.error('addUpload: enterpriseID is required but was undefined');
      return;
    }

    // Check if an upload for this file and partId already exists
    const existingUpload = state.uploads.find(u =>
      u.partId === partId &&
      u.file && u.file.name === file.name &&
      u.file.size === file.size &&
      (u.status === 'queued' || u.status === 'uploading')
    );

    // Also guard against duplicate adds for the same file/project/type even if partId differs (race conditions)
    const duplicateByFile = state.uploads.find(u =>
      u.type === (type || 'model') &&
      u.projectId === projectId &&
      u.file && u.file.name === file.name &&
      u.file.size === file.size &&
      (u.status === 'queued' || u.status === 'uploading')
    );

    if (existingUpload || duplicateByFile) {
      return;
    }

    const upload = {
      id: uuidv4(),
      file,
      status: 'queued',
      progress: 0,
      projectId,
      partId,
      replace,
      modelData,
      enterpriseID,
      type,
      storagePath,
      jobId,
      attachmentUuid,
      projectLabeling,
      task: null,
      watchdog: null,
      lastActivityAt: 0,
      cancelReason: null,
      countedDone: false,
    };

    commit('ADD_UPLOAD', upload);
    commit('OVERALL_ADD_TOTAL', 1);
    // Ensure visibility handler is installed
    ensureVisibilityHandler({ state, commit });
    dispatch('processQueue');
  },
  processQueue({ state, commit, dispatch }) {
    const isUploading = state.uploads.some(u => u.status === 'uploading');
    if (isUploading) return;

    const nextUpload = state.uploads.find(u => u.status === 'queued');
    if (nextUpload) {
      // Acquire wake lock to prevent tab throttling
      acquireWakeLock({ state, commit }).catch(() => {
        // Wake lock not available or failed - uploads will continue but may be throttled
        console.warn('Could not acquire wake lock - uploads may be slower when tab is backgrounded');
      });

      commit('UPDATE_UPLOAD_STATUS', { id: nextUpload.id, status: 'uploading' });

      const { file, projectId, partId, replace, modelData, enterpriseID, type, storagePath, jobId, attachmentUuid, projectLabeling } = nextUpload;
      const storage = getStorage();
      const path = storagePath || (type === 'model' ? `models/${partId}/${Date.now()}/${file.name}` : type === 'job' ? `jobs/${jobId}/${file.name}` : type === 'attachment' ? `attachments/${attachmentUuid}/${file.name}` : `uploads/${Date.now()}/${file.name}`);
      const storageRef = ref(storage, path);
      const uploadTask = uploadBytesResumable(storageRef, file);

      // Track the task so we can cancel it if needed
      commit('SET_UPLOAD_TASK', { id: nextUpload.id, task: uploadTask });
      commit('UPDATE_UPLOAD_ACTIVITY', { id: nextUpload.id });

      // Start a watchdog to fail stalled uploads (Firebase may retry silently)
      try {
        const watchdogId = setInterval(() => {
          try {
            const u = state.uploads.find(x => x.id === nextUpload.id);
            if (!u || u.status !== 'uploading') {
              clearInterval(watchdogId);
              commit('CLEAR_UPLOAD_WATCHDOG', nextUpload.id);
              return;
            }
            if (u.lastActivityAt && (Date.now() - u.lastActivityAt) > STALL_TIMEOUT_MS) {
              // Mark reason and cancel task to force error callback
              commit('SET_UPLOAD_CANCEL_REASON', { id: nextUpload.id, reason: 'stall' });
              try { uploadTask.cancel(); } catch (e) {}
            }
          } catch (e) {}
        }, 5000);
        commit('SET_UPLOAD_WATCHDOG', { id: nextUpload.id, watchdog: watchdogId });
      } catch (e) {}

      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          commit('UPDATE_UPLOAD_PROGRESS', { id: nextUpload.id, progress: progress });
          commit('UPDATE_UPLOAD_ACTIVITY', { id: nextUpload.id });
          // For jobs, mirror progress to job doc so legacy UI stays updated
          try {
            if (type === 'job' && enterpriseID && jobId) {
              updateDoc(doc(db, 'enterprises', enterpriseID, 'jobs', jobId), {
                uploadProgress: progress,
                dateModified: Date.now(),
                lastUpdatedBy: (auth && auth.currentUser && auth.currentUser.uid) || null,
              }).catch(() => {});
            }
            if (type === 'attachment' && attachmentUuid) {
              if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('upload:attachment:progress', { detail: { projectId, attachmentUuid, progress } }));
            }
            if (type === 'purchaseOrder') {
              if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('upload:purchaseOrder:progress', { detail: { projectId, partId, progress } }));
            }
          } catch (e) {}
        },
        (error) => {
          // Always clear watchdog on terminal error
          commit('CLEAR_UPLOAD_WATCHDOG', nextUpload.id);
          // Handle cancellation distinctly from other errors
          if (error && (error.code === 'storage/canceled' || error.code === 'storage/cancelled')) {
            const u = state.uploads.find(x => x.id === nextUpload.id);
            if (u && u.cancelReason === 'stall') {
              // Treat stall-induced cancel as failure and keep visible
              commit('UPDATE_UPLOAD_STATUS', { id: nextUpload.id, status: 'failed' });
              commit('CLEAR_UPLOAD_TASK', nextUpload.id);
              commit('CLEAR_UPLOAD_CANCEL_REASON', nextUpload.id);
              if (!u.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', nextUpload.id); commit('OVERALL_INCREMENT_COMPLETED', 1); }
              try {
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('upload:failed', { detail: nextUpload }));
                  window.dispatchEvent(new CustomEvent('upload:cancelled', { detail: nextUpload }));
                  if (u.type === 'job') window.dispatchEvent(new CustomEvent('upload:job:cancelled', { detail: { projectId: u.projectId, jobId: u.jobId } }));
                  if (u.type === 'attachment') window.dispatchEvent(new CustomEvent('upload:attachment:cancelled', { detail: { projectId: u.projectId, attachmentUuid: u.attachmentUuid } }));
                }
              } catch (e) {}
              dispatch('processQueue');
              return;
            } else {
              try { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('upload:cancelled', { detail: nextUpload })); } catch (e) {}
              commit('UPDATE_UPLOAD_STATUS', { id: nextUpload.id, status: 'cancelled' });
              commit('CLEAR_UPLOAD_TASK', nextUpload.id);
              if (u && !u.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', nextUpload.id); commit('OVERALL_INCREMENT_COMPLETED', 1); }
              try {
                if (u && u.type === 'job' && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('upload:job:cancelled', { detail: { projectId: u.projectId, jobId: u.jobId } }));
                if (u && u.type === 'attachment' && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('upload:attachment:cancelled', { detail: { projectId: u.projectId, attachmentUuid: u.attachmentUuid } }));
              } catch (e) {}
              // Remove cancelled upload immediately
              dispatch('removeCompletedUpload', nextUpload.id);
              dispatch('processQueue');
              return;
            }
          }

          console.error('Upload Error:', error);
          commit('UPDATE_UPLOAD_STATUS', { id: nextUpload.id, status: 'failed' });
          commit('CLEAR_UPLOAD_TASK', nextUpload.id);
          const u2 = state.uploads.find(x => x.id === nextUpload.id);
          if (u2 && !u2.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', nextUpload.id); commit('OVERALL_INCREMENT_COMPLETED', 1); }
          // On failure, perform same cleanup as cancel for project UI/state, and keep item visible
          try {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('upload:failed', { detail: nextUpload }));
              window.dispatchEvent(new CustomEvent('upload:cancelled', { detail: nextUpload }));
              if (type === 'attachment' && attachmentUuid) window.dispatchEvent(new CustomEvent('upload:attachment:failed', { detail: { projectId, attachmentUuid } }));
              if (type === 'purchaseOrder') window.dispatchEvent(new CustomEvent('upload:purchaseOrder:failed', { detail: { projectId, partId } }));
            }
          } catch (e) {}

          dispatch('processQueue');
        },
        async () => {
          // Clear watchdog on success
          commit('CLEAR_UPLOAD_WATCHDOG', nextUpload.id);
          getDownloadURL(uploadTask.snapshot.ref).then(async (downloadURL) => {

            // If this is a job upload, update the job doc and finish
            if (type === 'job' && enterpriseID && jobId) {
              try {
                await updateDoc(doc(db, 'enterprises', enterpriseID, 'jobs', jobId), {
                  url: downloadURL,
                  uploadProgress: 100,
                  dateModified: Date.now(),
                  lastUpdatedBy: (auth && auth.currentUser && auth.currentUser.uid) || null,
                });
              } catch (e) {}
              commit('UPDATE_UPLOAD_STATUS', { id: nextUpload.id, status: 'completed' });
              commit('CLEAR_UPLOAD_TASK', nextUpload.id);
              const u3j = state.uploads.find(x => x.id === nextUpload.id);
              if (u3j && !u3j.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', nextUpload.id); commit('OVERALL_INCREMENT_COMPLETED', 1); }
              setTimeout(() => { dispatch('removeCompletedUpload', nextUpload.id); }, 2000);
              dispatch('processQueue');
              return;
            }

            // If this is an attachment upload, emit completion and finish
            if (type === 'attachment' && attachmentUuid) {
              try {
                // Persist attachment URL to project document so completion survives navigation
                if (enterpriseID && projectId) {
                  await persistAttachmentCompletion({ enterpriseID, projectId, attachmentUuid, file, downloadURL });
                }
                if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('upload:attachment:completed', { detail: { projectId, attachmentUuid, url: downloadURL } }));
              } catch (e) {}
              commit('UPDATE_UPLOAD_STATUS', { id: nextUpload.id, status: 'completed' });
              commit('CLEAR_UPLOAD_TASK', nextUpload.id);
              const u3a = state.uploads.find(x => x.id === nextUpload.id);
              if (u3a && !u3a.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', nextUpload.id); commit('OVERALL_INCREMENT_COMPLETED', 1); }
              setTimeout(() => { dispatch('removeCompletedUpload', nextUpload.id); }, 2000);
              dispatch('processQueue');
              return;
            }

            // If this is a purchase order upload, emit completion and finish
            if (type === 'purchaseOrder') {
              console.log('Purchase order upload completed:', { projectId, partId, url: downloadURL });
              try {
                if (typeof window !== 'undefined') {
                  const event = new CustomEvent('upload:purchaseOrder:completed', { detail: { projectId, partId, url: downloadURL } });
                  window.dispatchEvent(event);
                  console.log('Purchase order completion event dispatched');
                }
              } catch (e) {
                console.error('Error dispatching purchase order completion event:', e);
              }
              commit('UPDATE_UPLOAD_STATUS', { id: nextUpload.id, status: 'completed' });
              commit('CLEAR_UPLOAD_TASK', nextUpload.id);
              const u3po = state.uploads.find(x => x.id === nextUpload.id);
              if (u3po && !u3po.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', nextUpload.id); commit('OVERALL_INCREMENT_COMPLETED', 1); }
              setTimeout(() => { dispatch('removeCompletedUpload', nextUpload.id); }, 2000);
              dispatch('processQueue');
              return;
            }

            // Default model behavior below
            // Validate projectId before creating document reference
            if (!projectId) {
              console.error('processQueue: projectId is undefined, cannot create document reference');
              commit('UPDATE_UPLOAD_STATUS', { id: nextUpload.id, status: 'failed' });
              commit('CLEAR_UPLOAD_TASK', nextUpload.id);
              dispatch('processQueue');
              return;
            }

            // Validate enterpriseID
            if (!enterpriseID) {
              console.error('processQueue: enterpriseID is undefined');
              commit('UPDATE_UPLOAD_STATUS', { id: nextUpload.id, status: 'failed' });
              commit('CLEAR_UPLOAD_TASK', nextUpload.id);
              dispatch('processQueue');
              return;
            }

            const projectRef = doc(db, 'enterprises', enterpriseID, 'projects', projectId);

            // Read project data to build the model, then queue for batched write
            try {
              const modelUuid = partId;
              const projectDoc = await getDoc(projectRef);

              if (!projectDoc.exists()) {
                console.error("Project document not found!");
                commit('UPDATE_UPLOAD_STATUS', { id: nextUpload.id, status: 'failed' });
                commit('CLEAR_UPLOAD_TASK', nextUpload.id);
                try {
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('upload:failed', { detail: nextUpload }));
                    window.dispatchEvent(new CustomEvent('upload:cancelled', { detail: nextUpload }));
                  }
                } catch (e) {}
                const u4 = state.uploads.find(x => x.id === nextUpload.id);
                if (u4 && !u4.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', nextUpload.id); commit('OVERALL_INCREMENT_COMPLETED', 1); }
                dispatch('processQueue');
                return;
              }

              const projectData = projectDoc.data();
              const existingModels = Array.isArray(projectData.models) ? projectData.models : [];

              // Build a complete model object
              const defaultSettings = (projectData.production && projectData.production.defaultPrintSettings) || {}
              const inheritSettings = (existingModels.length > 0 && existingModels[0].requestedSettings) || defaultSettings
              const inheritUnits = (existingModels.length > 0 && existingModels[0].units) || 'mm'

              // Determine if model should be staff-only
              const shouldMarkStaffOnly = (projectData.configPublic?.staffOnlyProjectStatuses || []).includes(projectData.status);
              const existingModelIndexForStaff = existingModels.findIndex(m => m.uuid === modelUuid);
              let staffOnlyFlag;
              if (replace && existingModelIndexForStaff > -1) {
                staffOnlyFlag = Boolean(existingModels[existingModelIndexForStaff].staffOnly);
              } else if (replace) {
                // use optional chaining to avoid missing state preventing uploads
                staffOnlyFlag = Boolean(app?.$root?.userProfile?.prefs_staffOnlyDefault);
              } else {
                staffOnlyFlag = Boolean(app?.$root?.userProfile?.prefs_staffOnlyDefault || projectData.checkout || shouldMarkStaffOnly);
              }

              const newModel = {
                uuid: modelUuid,
                name: modelData?.name || file.name,
                url: downloadURL,
                size: file.size,
                quantity: String(modelData?.quantity || '1'),
                notes: modelData?.notes || '',
                requestedSettings: modelData?.requestedSettings || inheritSettings || {},
                units: modelData?.units || inheritUnits,
                dateCreated: modelData?.dateCreated || Date.now(),
                dateModified: Date.now(),
                dateUploaded: Date.now(),
                fileLastModified: file.lastModified,
                lastUpdatedBy: (auth && auth.currentUser && auth.currentUser.uid) || null,
                uploadedBy: (auth && auth.currentUser && auth.currentUser.uid) || null,
                staffOnly: staffOnlyFlag,
                rawUrl: null,
                rawName: null,
                hiResUrl: null,
                cmUrl: null,
                cmSize: null,
                inchUrl: null,
                inchSize: null,
                thumbnails: null,
                thumbnailsLarge: null,
                thumbnailsSmall: null,
                thumbnailsOrbit: null
              };

              if (modelData?.createdBy) {
                newModel.createdBy = modelData.createdBy;
              }
              if (typeof (modelData && modelData.summary) === 'string') newModel.summary = modelData.summary;
              if (typeof (modelData && modelData.defaultSummary) === 'string') newModel.defaultSummary = modelData.defaultSummary;
              if (Array.isArray(modelData?.previousVersions)) {
                newModel.previousVersions = modelData.previousVersions;
              }

              // Queue the write to be batched with other uploads for the same project
              dispatch('queueModelWrite', {
                projectId,
                enterpriseID,
                uploadId: nextUpload.id,
                modelUuid,
                newModel,
                replace,
                file,
                projectLabeling
              });

              // Process next upload immediately (batched write will handle Firestore update)
              dispatch('processQueue');
            } catch (error) {
              console.error("Error preparing model for upload:", error);
              commit('UPDATE_UPLOAD_STATUS', { id: nextUpload.id, status: 'failed' });
              commit('CLEAR_UPLOAD_TASK', nextUpload.id);
              try {
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent('upload:failed', { detail: nextUpload }));
                  window.dispatchEvent(new CustomEvent('upload:cancelled', { detail: nextUpload }));
                }
              } catch (e) {}
              const u5 = state.uploads.find(x => x.id === nextUpload.id);
              if (u5 && !u5.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', nextUpload.id); commit('OVERALL_INCREMENT_COMPLETED', 1); }
              dispatch('processQueue');
            }
          });
        }
      );
    }
  },
  retryUploadAction({ commit, dispatch, state }, id) {
    const upload = state.uploads.find(u => u.id === id);
    if (!upload) return;
    if (upload.status !== 'failed' && upload.status !== 'cancelled') return;
    if (upload.countedDone) {
      commit('OVERALL_DECREMENT_COMPLETED', 1);
      commit('MARK_UPLOAD_OVERALL_UNCOUNTED', id);
    }
    commit('CLEAR_UPLOAD_TASK', id);
    commit('UPDATE_UPLOAD_PROGRESS', { id, progress: 0 });
    commit('UPDATE_UPLOAD_STATUS', { id, status: 'queued' });
    dispatch('processQueue');
  },
  cancelUploadAction({ commit, state, dispatch }, id) {
    const upload = state.uploads.find(u => u.id === id);
    if (upload) {
      if (upload.status === 'queued') {
        // Remove from queue immediately
        try { if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('upload:cancelled', { detail: upload })); } catch (e) {}
        try {
          if (upload.type === 'job' && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('upload:job:cancelled', { detail: { projectId: upload.projectId, jobId: upload.jobId } }));
          if (upload.type === 'attachment' && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('upload:attachment:cancelled', { detail: { projectId: upload.projectId, attachmentUuid: upload.attachmentUuid } }));
        } catch (e) {}
        if (!upload.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', id); commit('OVERALL_INCREMENT_COMPLETED', 1); }
        commit('REMOVE_UPLOAD', id);
        if (state.uploads.length === 0) commit('OVERALL_RESET');
        dispatch('checkAndReleaseWakeLock');
        dispatch('processQueue');
        return;
      }

      if (upload.status === 'uploading' && upload.task && typeof upload.task.cancel === 'function') {
        // Cancel the active upload; state will be updated in error handler
        try {
          upload.task.cancel();
        } catch (e) {
          console.error('Error while cancelling upload:', e);
          commit('UPDATE_UPLOAD_STATUS', { id, status: 'failed' });
          commit('CLEAR_UPLOAD_TASK', id);
          // On forced-cancel error, keep visible for user action
          if (!upload.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', id); commit('OVERALL_INCREMENT_COMPLETED', 1); }
          dispatch('checkAndReleaseWakeLock');
          dispatch('processQueue');
        }
        try {
          if (upload.type === 'job' && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('upload:job:cancelled', { detail: { projectId: upload.projectId, jobId: upload.jobId } }));
          if (upload.type === 'attachment' && typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('upload:attachment:cancelled', { detail: { projectId: upload.projectId, attachmentUuid: upload.attachmentUuid } }));
        } catch (e) {}
        return;
      }

      // If already completed/failed/cancelled, remove it from list
      if (['completed', 'failed', 'cancelled'].includes(upload.status)) {
        commit('REMOVE_UPLOAD', id);
        if (state.uploads.length === 0) commit('OVERALL_RESET');
        dispatch('checkAndReleaseWakeLock');
        dispatch('processQueue');
      }
    }
  },
  removeCompletedUpload({ commit, state }, id) {
    commit('REMOVE_UPLOAD', id);
    if (state.uploads.length === 0) {
      commit('OVERALL_RESET');
      // Release wake lock when all uploads are done
      releaseWakeLock({ state, commit });
    }
  },
  // Check if all uploads are done and release wake lock if so
  checkAndReleaseWakeLock({ state, commit }) {
    const hasActiveUploads = state.uploads.some(u => u.status === 'uploading' || u.status === 'queued');
    if (!hasActiveUploads) {
      releaseWakeLock({ state, commit });
    }
  },
  // Queue a model write to be batched with others for the same project
  queueModelWrite({ commit, dispatch }, { projectId, enterpriseID, uploadId, modelUuid, newModel, replace, file, projectLabeling }) {
    commit('ADD_PENDING_MODEL_WRITE', { projectId, enterpriseID, uploadId, modelUuid, newModel, replace, file, projectLabeling });
    // Initialize debounced flush lazily
    if (!debouncedFlush) {
      debouncedFlush = debounce(() => dispatch('flushPendingModelWrites'), BATCH_WRITE_DELAY_MS);
    }
    debouncedFlush();
  },
  // Flush all pending model writes in batched transactions (one per project)
  async flushPendingModelWrites({ state, commit, dispatch }) {
    const pendingByProject = { ...state.pendingModelWrites };
    commit('CLEAR_PENDING_MODEL_WRITES');

    for (const projectId of Object.keys(pendingByProject)) {
      const { enterpriseID, writes } = pendingByProject[projectId];
      if (!writes || writes.length === 0) continue;

      const projectRef = doc(db, 'enterprises', enterpriseID, 'projects', projectId);

      try {
        let projectNotFound = false;
        const completedModels = [];

        await runTransaction(db, async (transaction) => {
          const projectDoc = await transaction.get(projectRef);
          if (!projectDoc.exists()) {
            projectNotFound = true;
            return;
          }

          const projectData = projectDoc.data();
          const updatedModels = Array.isArray(projectData.models) ? [...projectData.models] : [];
          const modelDataUpdates = {};
          const projectUpdates = {};

          for (const write of writes) {
            const { modelUuid, newModel, replace, file, projectLabeling } = write;

            // Find if we're replacing or adding
            if (replace) {
              const modelIndex = updatedModels.findIndex(m => m.uuid === modelUuid);
              if (modelIndex > -1) {
                const merged = Object.assign({}, updatedModels[modelIndex], newModel);
                const conversionFields = ['rawUrl', 'rawName', 'hiResUrl', 'cmUrl', 'cmSize', 'inchUrl', 'inchSize'];
                conversionFields.forEach(field => {
                  if (merged[field] === null) delete merged[field];
                });
                merged.thumbnails = null;
                merged.thumbnailsLarge = null;
                merged.thumbnailsSmall = null;
                merged.thumbnailsOrbit = null;
                updatedModels[modelIndex] = merged;
              } else {
                const existingIndex = updatedModels.findIndex(m => m.uuid === newModel.uuid);
                if (existingIndex === -1) updatedModels.push(newModel);
                else {
                  const merged = Object.assign({}, updatedModels[existingIndex], newModel);
                  const conversionFields = ['rawUrl', 'rawName', 'hiResUrl', 'cmUrl', 'cmSize', 'inchUrl', 'inchSize'];
                  conversionFields.forEach(field => {
                    if (merged[field] === null) delete merged[field];
                  });
                  merged.thumbnails = null;
                  merged.thumbnailsLarge = null;
                  merged.thumbnailsSmall = null;
                  merged.thumbnailsOrbit = null;
                  updatedModels[existingIndex] = merged;
                }
              }
            } else {
              const existingIndex = updatedModels.findIndex(m => m.uuid === newModel.uuid);
              if (existingIndex === -1) updatedModels.push(newModel);
              else updatedModels[existingIndex] = Object.assign({}, updatedModels[existingIndex], newModel);
            }

            // Prepare modelData updates
            const nameNoExt = (newModel.name || '').split('.').slice(0, -1).join('.') || newModel.name;
            modelDataUpdates[`modelData.${modelUuid}.name`] = nameNoExt;
            modelDataUpdates[`modelData.${modelUuid}.originalFile`] = {
              name: newModel.name,
              url: newModel.url,
              size: file.size,
            };

            const ext = (newModel.name || '').toLowerCase().split('.').pop();
            if (['stl', 'obj'].includes(ext)) {
              const meshFile = { name: newModel.name, url: newModel.url, size: file.size };
              modelDataUpdates[`modelData.${modelUuid}.meshFile`] = meshFile;
              modelDataUpdates[`modelData.${modelUuid}.meshFile${ext.toUpperCase()}`] = meshFile;
            }

            // Apply project labeling if configured (only for first write that needs it)
            const effectiveProjectLabeling = (typeof projectLabeling !== 'undefined' ? projectLabeling : (projectData.configPublic && projectData.configPublic.projectLabeling));
            if (effectiveProjectLabeling && !projectData.label && !projectUpdates.label) {
              if (effectiveProjectLabeling === 'first-number') {
                const match = file.name.match(/\d+/);
                if (match) projectUpdates.label = match[0];
              }
            }

            completedModels.push({ modelUuid, newModel });
          }

          projectUpdates.models = updatedModels;
          // Top-level stamp, read inside the transaction so a retried attempt names whoever is signed in
          // when it commits. firestore.rules (F-113) refuses a guest upload without it.
          projectUpdates.lastUpdatedBy = currentWriterUid();
          transaction.update(projectRef, Object.assign(projectUpdates, modelDataUpdates));
        });

        // Handle results for each write in the batch
        for (const write of writes) {
          const { uploadId, modelUuid, newModel } = write;
          if (projectNotFound) {
            console.error("Project document not found!");
            commit('UPDATE_UPLOAD_STATUS', { id: uploadId, status: 'failed' });
            commit('CLEAR_UPLOAD_TASK', uploadId);
            try {
              if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('upload:failed', { detail: { id: uploadId } }));
                window.dispatchEvent(new CustomEvent('upload:cancelled', { detail: { id: uploadId } }));
              }
            } catch (e) {}
            const u = state.uploads.find(x => x.id === uploadId);
            if (u && !u.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', uploadId); commit('OVERALL_INCREMENT_COMPLETED', 1); }
          } else {
            // Dispatch event to notify ProjectPage that model is ready
            if (newModel) {
              window.dispatchEvent(new CustomEvent('upload:model:completed', {
                detail: { projectId, modelUuid, model: newModel }
              }));
            }

            commit('UPDATE_UPLOAD_STATUS', { id: uploadId, status: 'completed' });
            commit('CLEAR_UPLOAD_TASK', uploadId);
            const u = state.uploads.find(x => x.id === uploadId);
            if (u && !u.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', uploadId); commit('OVERALL_INCREMENT_COMPLETED', 1); }

            setTimeout(() => { dispatch('removeCompletedUpload', uploadId); }, 2000);
          }
        }
      } catch (error) {
        console.error("Error updating project document (batched):", error);
        // Mark all writes in this batch as failed
        for (const write of writes) {
          const { uploadId } = write;
          commit('UPDATE_UPLOAD_STATUS', { id: uploadId, status: 'failed' });
          commit('CLEAR_UPLOAD_TASK', uploadId);
          try {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(new CustomEvent('upload:failed', { detail: { id: uploadId } }));
              window.dispatchEvent(new CustomEvent('upload:cancelled', { detail: { id: uploadId } }));
            }
          } catch (e) {}
          const u = state.uploads.find(x => x.id === uploadId);
          if (u && !u.countedDone) { commit('MARK_UPLOAD_OVERALL_COUNTED', uploadId); commit('OVERALL_INCREMENT_COMPLETED', 1); }
        }
      }
    }

    dispatch('processQueue');
  },
};

const mutations = {
  ADD_UPLOAD(state, upload) {
    state.uploads.push(upload);
  },
  OVERALL_ADD_TOTAL(state, n) {
    state.overall.total += (n || 0);
  },
  OVERALL_INCREMENT_COMPLETED(state, n) {
    state.overall.completed += (n || 0);
    if (state.overall.completed > state.overall.total) state.overall.completed = state.overall.total;
  },
  OVERALL_DECREMENT_COMPLETED(state, n) {
    state.overall.completed -= (n || 0);
    if (state.overall.completed < 0) state.overall.completed = 0;
  },
  OVERALL_RESET(state) {
    state.overall.total = 0;
    state.overall.completed = 0;
  },
  UPDATE_UPLOAD_STATUS(state, { id, status }) {
    const upload = state.uploads.find((u) => u.id === id);
    if (upload) {
      upload.status = status;
    }
  },
  UPDATE_UPLOAD_PROGRESS(state, { id, progress }) {
    const upload = state.uploads.find((u) => u.id === id);
    if (upload) {
      upload.progress = progress;
    }
  },
  MARK_UPLOAD_OVERALL_COUNTED(state, id) {
    const upload = state.uploads.find((u) => u.id === id);
    if (upload) upload.countedDone = true;
  },
  MARK_UPLOAD_OVERALL_UNCOUNTED(state, id) {
    const upload = state.uploads.find((u) => u.id === id);
    if (upload) upload.countedDone = false;
  },
  SET_UPLOAD_TASK(state, { id, task }) {
    const upload = state.uploads.find((u) => u.id === id);
    if (upload) {
      upload.task = task;
    }
  },
  CLEAR_UPLOAD_TASK(state, id) {
    const upload = state.uploads.find((u) => u.id === id);
    if (upload) {
      upload.task = null;
    }
  },
  REMOVE_UPLOAD(state, id) {
    const index = state.uploads.findIndex((u) => u.id === id);
    if (index > -1) {
      const u = state.uploads[index];
      // Safety: clear any timers
      if (u && u.watchdog) {
        try { clearInterval(u.watchdog); } catch (e) {}
      }
      state.uploads.splice(index, 1);
    }
  },
  SET_UPLOAD_WATCHDOG(state, { id, watchdog }) {
    const upload = state.uploads.find((u) => u.id === id);
    if (upload) {
      // Clear existing
      if (upload.watchdog) { try { clearInterval(upload.watchdog); } catch (e) {} }
      upload.watchdog = watchdog;
    }
  },
  CLEAR_UPLOAD_WATCHDOG(state, id) {
    const upload = state.uploads.find((u) => u.id === id);
    if (upload) {
      if (upload.watchdog) { try { clearInterval(upload.watchdog); } catch (e) {} }
      upload.watchdog = null;
    }
  },
  UPDATE_UPLOAD_ACTIVITY(state, { id }) {
    const upload = state.uploads.find((u) => u.id === id);
    if (upload) {
      upload.lastActivityAt = Date.now();
    }
  },
  SET_UPLOAD_CANCEL_REASON(state, { id, reason }) {
    const upload = state.uploads.find((u) => u.id === id);
    if (upload) {
      upload.cancelReason = reason || null;
    }
  },
  CLEAR_UPLOAD_CANCEL_REASON(state, id) {
    const upload = state.uploads.find((u) => u.id === id);
    if (upload) {
      upload.cancelReason = null;
    }
  },
  SET_WAKE_LOCK(state, wakeLock) {
    state.wakeLock = wakeLock;
  },
  SET_VISIBILITY_WARNING_SHOWN(state, shown) {
    state.visibilityWarningShown = shown;
  },
  ADD_PENDING_MODEL_WRITE(state, { projectId, enterpriseID, uploadId, modelUuid, newModel, replace, file, projectLabeling }) {
    if (!state.pendingModelWrites[projectId]) {
      state.pendingModelWrites[projectId] = { enterpriseID, writes: [] };
    }
    state.pendingModelWrites[projectId].writes.push({ uploadId, modelUuid, newModel, replace, file, projectLabeling });
  },
  CLEAR_PENDING_MODEL_WRITES(state) {
    state.pendingModelWrites = {};
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
